import React, { Suspense, useEffect, useState } from 'react';
import { Environment } from 'relay-runtime';

import type { TasksQuery$variables } from '../../src/graphql/__generated__/TasksQuery.graphql';
import { DEFAULT_PAGE_SIZE } from '../const';
import useDispatchSetterStore from '../hooks/useDispatchSetterStore';
import useDynamicSelector from '../hooks/useDynamicSelector';
import { IGraphQLTableLoader } from '../hooks/useGraphQLLoader';
import PropsTableProvider from '../providers/propsTableProvider';
import { getSetterTable, ITableState } from '../store/slices/tableSlice';
import { ActionsType, OnScheduleItemsImport, OnTasksImport } from '../types';
import TableDb from './tableDb';
import WrapperTableDBLoader, { LoaderVarsType } from './warpperTableDBLoader';
import WrapperTableDb from './WrapperTableDb';

export interface IOutTableDb<T extends TasksQuery$variables, K, E> extends IGraphQLTableLoader<T, K, E> {
    actions?: ActionsType[];
    environment?: Environment;
    prefixStore: string;
    onScheduleItemsImport?: OnScheduleItemsImport;
    onTasksImport?: OnTasksImport;
}

const TableMiddleware = <T extends TasksQuery$variables, K, E>({
    query,
    fragmentList,
    fragment,
    fragmentConnection,
    nameQuery,
    columnsLabel,
    paramFilter,
    paramSorter,
    actions,
    customActions,
    customActionComponents,
    extendRow,
    extendRowByField,
    varRequest,
    sizeOfPage,
    prefixStore,
    renderDataColumn,
    externalFetchKey,

    mutationUpdate,
    mutationCreate,
    mutationDelete,
    onClickRow,
    heightTable,
    onSelectionChange,
    onSelect,
    fixedColumn,
    defaultHiddenColumn,
    onScheduleItemsImport,
    onTasksImport,
    prefixFetchKey,
}: IOutTableDb<T, K, E>) => {
    const setterTable = useDispatchSetterStore<ITableState>(getSetterTable(nameQuery + prefixStore));
    const pageSize = useDynamicSelector('table', 'pageSize');
    const [vars, setVars] = useState<LoaderVarsType>();

    const [dataTableFromServer, setDataTableFromServer] = useState<{
        rows: any[];
        count: number;
        connectionId: string;
        columns: any[];
    }>({ columns: [], rows: [], count: 0, connectionId: '' });

    const isAddColumnAction =
        actions?.includes('edit') ||
        actions?.includes('delete') ||
        !!customActions?.length ||
        !!customActionComponents?.length;

    useEffect(() => {
        setterTable({ prefixFetchKey });
    }, [prefixFetchKey]);

    useEffect(() => {
        if (pageSize === 1) {
            const [firstSizeOfPage] = sizeOfPage || [];
            setterTable({ pageSize: +firstSizeOfPage || +DEFAULT_PAGE_SIZE });
        }
    }, [sizeOfPage]);

    return (
        <PropsTableProvider nameQuery={nameQuery} prefixStore={prefixStore}>
            <Suspense
                fallback={
                    <WrapperTableDb
                        actions={actions}
                        sizeOfPage={sizeOfPage}
                        renderDataColumn={renderDataColumn}
                        heightTable={heightTable}
                        defaultHiddenColumn={defaultHiddenColumn}
                        filterFlexible={vars?.filterFlexible}
                        orderBy={vars?.orderBy}
                    />
                }
            >
                <WrapperTableDBLoader<T, K, E>
                    query={query}
                    fragmentList={fragmentList}
                    fragment={fragment}
                    fragmentConnection={fragmentConnection}
                    nameQuery={nameQuery}
                    columnsLabel={columnsLabel}
                    paramFilter={paramFilter}
                    paramSorter={paramSorter}
                    isAddColumnAction={isAddColumnAction}
                    sizeOfPage={sizeOfPage}
                    onUpdateData={(rows, count, connectionId, columns) => {
                        setDataTableFromServer({
                            columns: columns || [],
                            rows,
                            count,
                            connectionId,
                        });
                    }}
                    extendRow={extendRow}
                    extendRowByField={extendRowByField}
                    varRequest={varRequest}
                    externalFetchKey={externalFetchKey}
                    fixedColumn={fixedColumn}
                    setVars={setVars}
                />
                {dataTableFromServer.columns.length && (
                    <TableDb
                        columns={dataTableFromServer.columns}
                        dataRows={dataTableFromServer.rows}
                        count={dataTableFromServer.count}
                        actions={actions}
                        customActions={customActions}
                        customActionComponents={customActionComponents}
                        connectionId={dataTableFromServer.connectionId}
                        mutationUpdate={mutationUpdate}
                        mutationCreate={mutationCreate}
                        mutationDelete={mutationDelete}
                        sizeOfPage={sizeOfPage}
                        paramFilter={paramFilter}
                        renderDataColumn={renderDataColumn}
                        onClickRow={onClickRow}
                        heightTableBody={heightTable}
                        onSelectionChange={onSelectionChange}
                        onSelect={onSelect as any} //ToDo type any should be fix
                        defaultHiddenColumn={defaultHiddenColumn}
                        filterFlexible={vars?.filterFlexible}
                        orderBy={vars?.orderBy}
                        onScheduleItemsImport={onScheduleItemsImport}
                        onTasksImport={onTasksImport}
                    />
                )}
            </Suspense>
        </PropsTableProvider>
    );
};

export default TableMiddleware;
